import { useQuery } from '@tanstack/react-query';
import { fetchContactsWithPhoneNumber } from 'Modules/contacts/requests';
import { cleanPhoneNumber } from 'Modules/conversation/createConversation/components/createConversationForm/utils';
import { mapContactToPhoneNumbers } from 'Modules/softphone/resolver/phoneNumberResolver';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { TransferTarget } from 'Modules/softphone/types';
import { createLogger } from 'Modules/softphone/utils/logger';
import { useEffect, useRef } from 'react';

const logger = createLogger('useResolveTransferNumber');

const QUERY_KEY_RESOLVE_TRANSFER_NUMBER = 'softphone.resolveTransferNumber';

/**
 * Hook that resolves a manually entered phone number to an internal (directory)
 * contact. When the transfer target is of type 'number', this hook queries the
 * MatchContactByPhoneNumber API to check if the number belongs to an internal
 * contact with voicemail enabled.
 *
 * If a matching directory contact is found, the transfer target in the store is
 * upgraded from type 'number' to type 'contact' with voicemail information,
 * enabling the "Transfer to voicemail" option per FR10.
 *
 * This aligns the new softphone behavior with the legacy softphone, which
 * resolves manually entered numbers to internal contacts for voicemail transfer.
 */
export function useResolveTransferNumber(): void {
  const transferTarget = useSoftphoneStore((s) => s.transferTarget);
  const setTransferTarget = useSoftphoneStore((s) => s.setTransferTarget);
  const setTransferSelectedPhoneNumber = useSoftphoneStore((s) => s.setTransferSelectedPhoneNumber);

  // Clean the phone number for the API query
  const rawNumber = transferTarget?.type === 'number'
    ? transferTarget.phoneNumbers[0]?.number ?? ''
    : '';
  const cleanedNumber = cleanPhoneNumber(rawNumber);

  // Track which number we've already resolved to avoid re-processing
  const resolvedNumberRef = useRef<string | null>(null);

  const { data: matchedContacts } = useQuery({
    queryKey: [QUERY_KEY_RESOLVE_TRANSFER_NUMBER, cleanedNumber],
    queryFn: () => fetchContactsWithPhoneNumber(cleanedNumber),
    enabled: !!cleanedNumber && cleanedNumber.length >= 3,
  });

  useEffect(() => {
    // Only process when we have a number target and API results
    if (!transferTarget || transferTarget.type !== 'number') {
      resolvedNumberRef.current = null;
      return;
    }

    if (!matchedContacts || matchedContacts.length === 0) {
      return;
    }

    // Avoid re-processing the same number
    if (resolvedNumberRef.current === cleanedNumber) {
      return;
    }

    // Find the first directory contact (source === 'Bhive') with voicemail
    const directoryContact = matchedContacts.find(
      (contact) => contact.source === 'Bhive'
    );

    if (!directoryContact) {
      logger.info('No directory contact found for number', { cleanedNumber });
      resolvedNumberRef.current = cleanedNumber;
      return;
    }

    // Mark as resolved
    resolvedNumberRef.current = cleanedNumber;

    const phoneNumbers = mapContactToPhoneNumbers(directoryContact);
    const displayName =
      directoryContact.name ||
      [directoryContact.firstName, directoryContact.lastName].filter(Boolean).join(' ') ||
      transferTarget.displayName;

    // Find the best matching phone number from the resolved contact to use
    // as the selected phone number (preserves the user's intent).
    const matchingPhone = phoneNumbers.find((phone) => {
      const cleanedPhone = cleanPhoneNumber(phone.number);
      return cleanedPhone.includes(cleanedNumber) || cleanedNumber.includes(cleanedPhone);
    });

    const resolvedTarget: TransferTarget = {
      type: 'contact',
      displayName,
      contactId: directoryContact.id,
      sourceId: directoryContact.sourceId,
      contactType: 'directory',
      phoneNumbers: phoneNumbers.length > 0 ? phoneNumbers : transferTarget.phoneNumbers,
      hasVoicemail: !!directoryContact.voicemailInboxId,
      voicemailInboxId: directoryContact.voicemailInboxId,
    };

    logger.info('Resolved manual number to directory contact', {
      cleanedNumber,
      displayName: resolvedTarget.displayName,
      hasVoicemail: resolvedTarget.hasVoicemail,
      voicemailInboxId: resolvedTarget.voicemailInboxId,
    });

    setTransferTarget(resolvedTarget);

    // Update selected phone number to the matching number from the resolved contact
    if (matchingPhone) {
      setTransferSelectedPhoneNumber(matchingPhone.number);
    }
  }, [transferTarget, matchedContacts, cleanedNumber, setTransferTarget, setTransferSelectedPhoneNumber]);
}
